// The audience builder shared by campaign creation, the audience library and the planner: the
// type tabs, the rule editor (`./audience-rules-editor`), the live size gauge
// (`./audience-size-gauge`) and the confirm step. Visual rows are resolved to canonical API rules
// before any preview or save request leaves the browser; the rule fields and their validation are
// pure, in `@/lib/audience-rule-fields`.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '@/components/primitives';
import { SearchableDropdown } from '@/components/dropdowns';
import {
  RequiredField,
  useRequiredFeedback,
} from '@/components/required-field';
import {
  useAudiences,
  useAudienceEstimateSize,
  useAudiencePreview,
  usePixels,
  useProductions,
} from '@/api/hooks';
import type { Audience, Pixel, Production, RuleGroup } from '@/api/types';
import type { AudiencePreviewInput } from '@/api/manage-types';
import type { InsightStatus } from '@/api/types.gen';
import {
  AudienceVisualRuleError,
  resolveVisualAudienceRules,
  visualGroupsFromCanonical,
  visualTypeFromAudienceType,
  type VisualAudienceGroup,
  type VisualAudienceType,
} from '@/lib/audience-visual-rules';
import type { EncodedAudienceRules } from '@/lib/audience-rule-codec';
import {
  RULE_FIELDS,
  TYPE_BLURB,
  canAddAudienceGroup,
  defaultGroupsForType,
  defaultRuleValue,
  eligibleLookalikeSeeds,
  firstAudienceRuleIssue,
  prioritizeProduction,
} from '@/lib/audience-rule-fields';
import {
  LookalikeFactorBreakdown,
  LookalikeSelection,
} from '@/components/lookalike-explainer';
import {
  canonicalizeLookalikeShare,
  completedAudiencePreview,
  curvePointAt,
  firstLookalikeShareBps,
  lookalikeFitPollInterval,
  lookalikeModelOf,
  prospectPreviewBlocker,
  prospectPreviewBlockerMessage,
  resolvedAudiencePreviewCount,
} from '@/lib/lookalike-model';
import { RulesEditor } from './audience-rules-editor';
import { AudienceSizeGauge } from './audience-size-gauge';

// Stable while queries load: fresh fallback arrays invalidate the rule resolver
// and repeatedly notify the planner parent, creating a render loop on slow links.
const EMPTY_AUDIENCES: readonly Audience[] = [];
const EMPTY_PRODUCTIONS: readonly Production[] = [];
const EMPTY_PIXELS: readonly Pixel[] = [];

const TAB_ORDER: readonly VisualAudienceType[] = ['Retargeting', 'Direct', 'Lookalike'];

/** Why a type cannot be picked here, per type (a destination that cannot carry it). */
export type DisabledAudienceTypes = Partial<Record<VisualAudienceType, string>>;

export interface AudienceTypeTabsProps {
  type: VisualAudienceType;
  setType: (type: VisualAudienceType) => void;
  locked?: boolean;
  lockHint?: boolean;
  disabledTypes?: DisabledAudienceTypes;
}

function AudienceTypeTabs({ type, setType, locked = false, lockHint = false, disabledTypes = {} }: AudienceTypeTabsProps) {
  return (
    <div className="aud-tabs">
      {TAB_ORDER.map((tab) => {
        const isCurrent = type === tab;
        const isLockedOther = locked && !isCurrent;
        const disabledReason = disabledTypes[tab] || '';
        const isDisabled = !!disabledReason;
        return (
          <div
            key={tab}
            role="button"
            tabIndex={isLockedOther || isDisabled ? -1 : 0}
            aria-pressed={isCurrent}
            onKeyDown={event=>{if((event.key==='Enter'||event.key===' ')&&!locked&&!isDisabled){event.preventDefault();setType(tab);}}}
            className={`aud-tab ${isCurrent ? 'on' : ''} ${locked && isCurrent ? 'locked-current' : ''} ${isLockedOther ? 'locked-other' : ''} ${isDisabled ? 'unavailable' : ''}`}
            onClick={() => { if (!locked && !isDisabled) setType(tab); }}
            title={disabledReason || (isLockedOther
              ? `Audience type is locked to ${type} since launch — duplicate the campaign to switch.`
              : '')}
            aria-disabled={isLockedOther || isDisabled ? 'true' : undefined}
          >
            <h4 style={{display:'flex',alignItems:'center',gap:6}}>
              <span>{tab}</span>
              {locked && isCurrent && (
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{color:'var(--ink-2)'}}>
                  <rect x="3" y="7.5" width="10" height="6" rx="1"/><path d="M5.5 7.5V5a2.5 2.5 0 0 1 5 0v2.5"/>
                </svg>
              )}
              {lockHint && isCurrent && (
                <span className="lock-hint" style={{margin:0,marginLeft:'auto',padding:'1px 6px',fontSize:9}}>Locks at launch</span>
              )}
            </h4>
            <p>{TYPE_BLURB[tab]}</p>
            {isDisabled && (
              <span className="aud-tab-unavailable-note">— {disabledReason}</span>
            )}
            {locked && isCurrent && (
              <span className="lock-flag">— Locked since launch · rules below stay editable</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** What the builder reports to its parent on every change of the live preview. */
export interface AudiencePreviewState {
  count: number | null;
  totalReach: number | null;
  isProspecting: boolean;
  modelStatus: InsightStatus | null;
  fitRequestedAt: string | null;
  isPending: boolean;
  /** Every failure, an unfinished rule included; gates that refuse to continue read this. */
  error: Error | null;
  /** What an unfinished rule still needs, in the rule editor's own words; null once every rule
   * resolves. The size gauge shows it as INCOMPLETE rather than as a failed estimate. */
  incomplete: string | null;
  rules: RuleGroup | null;
  segments: EncodedAudienceRules['segments'];
}

export interface AudienceBuilderProps {
  /** The campaign's production, offered first in the production pickers; null outside one. */
  production: Production | null;
  confirmed: boolean;
  onConfirm?: () => void;
  onEdit?: () => void;
  /** Reference lists; each one the caller leaves out is read here. */
  audiences?: readonly Audience[];
  productions?: readonly Production[];
  pixels?: readonly Pixel[];
  /** Controlled type and groups; left out, the builder keeps its own. */
  type?: VisualAudienceType;
  setType?: (type: VisualAudienceType) => void;
  groups?: VisualAudienceGroup[];
  setGroups?: (groups: VisualAudienceGroup[]) => void;
  hideSavedPicker?: boolean;
  typeLocked?: boolean;
  lockHint?: boolean;
  onPreviewChange?: (preview: AudiencePreviewState) => void;
  actionMode?: 'confirm' | 'analyze';
  actionBusy?: boolean;
  disabledTypes?: DisabledAudienceTypes;
}

function AudienceBuilder({
  production,
  confirmed,
  onConfirm,
  onEdit = undefined,
  audiences = undefined,
  productions = undefined,
  pixels = undefined,
  type: typeProp,
  setType: setTypeProp,
  groups: groupsProp,
  setGroups: setGroupsProp,
  hideSavedPicker = false,
  typeLocked = false,
  lockHint = false,
  onPreviewChange,
  actionMode = 'confirm',
  actionBusy = false,
  disabledTypes = undefined,
}: AudienceBuilderProps) {
  const [typeInner, setTypeInner] = useState<VisualAudienceType>('Direct');
  const [groupsInner, setGroupsInner] = useState<VisualAudienceGroup[]>(() => defaultGroupsForType('Direct'));
  const type = typeProp !== undefined ? typeProp : typeInner;
  const setType = setTypeProp || setTypeInner;
  const groups = groupsProp !== undefined ? groupsProp : groupsInner;
  const setGroups = setGroupsProp || setGroupsInner;
  const [seededFrom, setSeededFrom] = useState<{ id: string; name: string } | null>(null);
  const required = useRequiredFeedback();
  const previewCallback = useRef(onPreviewChange);
  useEffect(() => {
    previewCallback.current = onPreviewChange;
  }, [onPreviewChange]);
  const { data: fetchedAudiences = EMPTY_AUDIENCES } = useAudiences();
  const { data: fetchedProductions = EMPTY_PRODUCTIONS } = useProductions();
  const { data: fetchedPixels = EMPTY_PIXELS } = usePixels();
  const saved = audiences || fetchedAudiences;
  const productionList = productions || fetchedProductions;
  const pixelList = pixels || fetchedPixels;
  const resolverProductions = useMemo(
    () => prioritizeProduction(production, productionList),
    [production, productionList],
  );
  const resolverContext = useMemo(
    () => ({ audiences: saved, productions: resolverProductions, pixels: pixelList }),
    [pixelList, resolverProductions, saved],
  );

  const resolution = useMemo((): { encoded: EncodedAudienceRules | null; error: Error | null } => {
    try {
      return { encoded: resolveVisualAudienceRules(groups, resolverContext), error: null };
    } catch (error) {
      return {
        encoded: null,
        error: error instanceof Error
          ? error
          : new AudienceVisualRuleError('Could not resolve audience rules'),
      };
    }
  }, [groups, resolverContext]);

  const [debouncedRules, setDebouncedRules] = useState<RuleGroup | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedRules(resolution.encoded?.rules ?? null),
      350,
    );
    return () => window.clearTimeout(timer);
  }, [resolution.encoded]);

  // A prospecting tree takes a different request, and the reason is the size selection.
  //
  // `/audiences/estimate-size` returns only `{count, total_reach}`, which cannot carry the size
  // curve — so a lookalike goes to `/audiences/preview` for the model projection instead. The
  // rules are CANONICALIZED first: the request object is hashed into the TanStack query key, so
  // leaving the chosen share in it would refetch on every change to the size dropdown, six
  // requests to learn six numbers the first response already carried.
  const lookalikeShareBps = firstLookalikeShareBps(resolution.encoded?.rules);
  const isProspecting = lookalikeShareBps !== null;
  const canonicalRules = debouncedRules && isProspecting
    ? canonicalizeLookalikeShare(debouncedRules)
    : debouncedRules;

  const estimateRequest: AudiencePreviewInput | null = canonicalRules && !isProspecting
    ? { rules: canonicalRules, projections: [{ kind: 'count' }] }
    : null;
  const previewRequest: AudiencePreviewInput | null = canonicalRules && isProspecting
    ? {
      rules: canonicalRules,
      projections: [{ kind: 'count' }, { kind: 'lookalike_model' }],
    }
    : null;
  const estimate = useAudienceEstimateSize(estimateRequest);
  // While the fit is pending the preview polls itself: composing is what
  // requested the accelerated fit, so the curve appears live in minutes and
  // the poll stops on the first ready response.
  const modelPreview = useAudiencePreview(previewRequest, true, (data) =>
    lookalikeFitPollInterval(data?.breakdowns, 5_000));
  const lookalikeModel = lookalikeModelOf(modelPreview.data?.breakdowns);

  // The size shown is read out of the curve at the operator's chosen share, not out of the
  // response — the response describes the pinned share. This is the whole point of publishing a
  // curve: changing the selection is a local read.
  const lookalikePoint = lookalikeModel && lookalikeShareBps !== null
    ? curvePointAt(lookalikeModel, lookalikeShareBps)
    : null;

  // Why this prospect preview cannot be used, if it cannot. Both the gate and the message
  // read from this, so they cannot disagree about whether the audience has a size.
  const prospectBlocker = isProspecting
    ? prospectPreviewBlocker(lookalikeModel?.status ?? null, lookalikePoint)
    : null;
  const activePreviewData = completedAudiencePreview(
    isProspecting,
    prospectBlocker,
    estimate.data,
    modelPreview.data,
  );
  const previewCount = resolvedAudiencePreviewCount(
    isProspecting,
    prospectBlocker,
    lookalikePoint,
    estimate.data,
  );
  const previewTotalReach = activePreviewData?.total_reach ?? null;

  const previewError = resolution.error
    || (isProspecting ? modelPreview.error : estimate.error)
    || null;
  // A rule still being filled in is not a failed estimate: no request was made. The gauge reads
  // this to say INCOMPLETE with the rule's own sentence; ERROR stays for a refused estimate.
  const incomplete = useMemo(
    () => resolution.error
      ? firstAudienceRuleIssue(groups, type, resolution.error, resolverContext)?.message
        ?? resolution.error.message
      : null,
    [groups, resolution.error, resolverContext, type],
  );
  // Compared on the CANONICALIZED tree. Comparing the raw one makes every change to the size
  // dropdown flash "CALCULATING" and disable Save for 350ms on an interaction that performs no
  // work at all.
  const pendingSource = isProspecting ? modelPreview : estimate;
  const canonicalEncoded = resolution.encoded?.rules && isProspecting
    ? canonicalizeLookalikeShare(resolution.encoded.rules)
    : resolution.encoded?.rules;
  const previewPending = !resolution.error
    && (JSON.stringify(canonicalRules) !== JSON.stringify(canonicalEncoded)
      || pendingSource.isFetching);

  useEffect(() => {
    previewCallback.current?.({
      count: previewCount,
      totalReach: previewTotalReach,
      isProspecting,
      modelStatus: isProspecting ? lookalikeModel?.status ?? 'unavailable' : null,
      fitRequestedAt: isProspecting ? lookalikeModel?.fit_requested_at ?? null : null,
      isPending: previewPending,
      error: previewError,
      incomplete,
      rules: resolution.encoded?.rules ?? null,
      segments: resolution.encoded?.segments ?? [],
    });
  }, [
    previewCount,
    previewTotalReach,
    previewError,
    incomplete,
    previewPending,
    isProspecting,
    lookalikeModel?.status,
    lookalikeModel?.fit_requested_at,
    resolution.encoded,
  ]);

  const loadSaved = (audience: Audience | undefined) => {
    if (!audience) return;
    const nextType = visualTypeFromAudienceType(audience.audience_type);
    const disabledReason = disabledTypes?.[nextType];
    if (disabledReason) {
      required.report([{
        key: 'audience-rules',
        message: `${nextType} audiences are unavailable here: ${disabledReason}.`,
      }]);
      return;
    }
    required.clearAll();
    setType(nextType);
    setGroups(visualGroupsFromCanonical(audience.rules));
    setSeededFrom({ id: audience.id, name: audience.name });
  };
  const onTypeChange = (nextType: VisualAudienceType) => {
    required.clearAll();
    setType(nextType);
    setGroups(defaultGroupsForType(nextType, resolverContext));
    setSeededFrom(null);
    onEdit?.();
  };
  const canConfirm = !!resolution.encoded
    && !!activePreviewData
    && !previewPending
    && !previewError;
  const tryConfirm = () => {
    if (resolution.error) {
      required.report([
        firstAudienceRuleIssue(groups, type, resolution.error, resolverContext),
      ]);
      return;
    }
    if (previewPending) {
      required.report([{
        key: 'audience-estimate',
        message: 'Wait for the live audience estimate to finish before continuing.',
      }]);
      return;
    }
    const activePreviewError = isProspecting ? modelPreview.error : estimate.error;
    if (activePreviewError) {
      required.report([{
        key: 'audience-estimate',
        message: activePreviewError.message || 'The live estimate failed. Retry after fixing the error above.',
      }]);
      return;
    }
    if (!activePreviewData) {
      required.report([{
        key: 'audience-estimate',
        message: prospectBlocker
          ? prospectPreviewBlockerMessage(prospectBlocker)
          : isProspecting
            ? 'A completed lookalike model preview is required before continuing.'
            : 'A completed live audience estimate is required before continuing.',
      }]);
      return;
    }
    required.clearAll();
    onConfirm?.();
  };

  return (
    <div>
      {!hideSavedPicker && !typeLocked && (
        <div className="facet" style={{margin:'0 0 12px',padding:'10px 12px',background:'var(--bg-sunk)'}}>
          <div className="facet-hd" style={{marginBottom:6}}>
            <h5 style={{fontSize:10}}>Use saved audience</h5>
            <span className="muted" style={{fontSize:10}}>Pre-fill rules from a saved audience · you can still tweak after loading</span>
          </div>
          <div className="flex gap-2 items-center" style={{flexWrap:'wrap'}}>
            <div style={{flex:'1 1 280px',minWidth:220}}>
              <SearchableDropdown
                value={seededFrom?.id || ''}
                onChange={(id) => {
                  const audience = saved.find((entry) => entry.id === id);
                  if (audience) loadSaved(audience);
                  else setSeededFrom(null);
                }}
                options={saved.map((audience) => ({
                  id: audience.id,
                  label: audience.name,
                  sublabel: `${visualTypeFromAudienceType(audience.audience_type)} · ${(audience.estimated_size ?? 0).toLocaleString()} people`,
                }))}
                placeholder="— Start from scratch —"
                emptyOptionLabel="— Start from scratch —"
                emptyOptionValue=""
                searchPlaceholder={`Search ${saved.length} saved audiences…`}
              />
            </div>
            {seededFrom && (
              <span className="tag" style={{background:'color-mix(in oklch, var(--accent) 14%, transparent)',borderColor:'transparent',color:'var(--ink)'}}>
                Loaded from <b style={{marginLeft:4}}>{seededFrom.name}</b>
              </span>
            )}
          </div>
        </div>
      )}

      <AudienceTypeTabs
        type={type}
        setType={onTypeChange}
        locked={typeLocked}
        lockHint={lockHint}
        disabledTypes={disabledTypes}
      />
      <RulesEditor
        type={type}
        groups={groups}
        setGroups={(next) => {
          required.clearAll();
          setGroups(next);
          onEdit?.();
        }}
        production={production}
        productions={resolverProductions}
        audiences={saved}
        pixels={pixelList}
        validationMessages={required.messages}
        validationAttempt={required.attempt}
      />

      <RequiredField
        fieldKey="audience-estimate"
        message={required.messages['audience-estimate']}
        attempt={required.attempt}
        className="mt-3"
        surface
      >
        <AudienceSizeGauge
          size={previewCount}
          totalReach={previewTotalReach}
          loading={previewPending}
          error={previewError}
          incomplete={incomplete}
          modelStatus={isProspecting && modelPreview.data
            ? lookalikeModel?.status ?? 'unavailable'
            : null}
          fitRequestedAt={isProspecting ? lookalikeModel?.fit_requested_at ?? null : null}
        />
      </RequiredField>

      {/*
        Inline, beside the number it explains. A prospect audience's size is modelled rather
        than counted and includes people who are already customers, so the operator needs the
        caveats where they read the figure — not one screen away in the Planner.

        Read-only here: the size dropdown in the rule rows above is the control, and its options
        are exactly the published curve stops. So changing the size is a local read of a curve
        this response already carried, which is why no request is issued.
      */}
      {isProspecting && lookalikeModel && (
        <div className="panel mt-3">
          <div className="panel-hd">
            <div>
              <h3>How this lookalike is assembled</h3>
              <div className="audience-planner-panel-sub">
                The factors that separate your seed audience from everyone else
              </div>
            </div>
          </div>
          <div className="panel-body" style={{ display: 'grid', gap: 14 }}>
            <LookalikeSelection
              model={lookalikeModel}
              shareBps={lookalikeShareBps ?? lookalikeModel.selection.share_bps}
            />
            {lookalikeModel.status === 'ready' && (
              <LookalikeFactorBreakdown model={lookalikeModel} />
            )}
          </div>
        </div>
      )}

      <div className="flex between items-center mt-3 audience-builder-foot" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5}}>
          {confirmed
            ? actionMode === 'analyze'
              ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> Analysis is current for this audience definition.</>
              : <><span style={{color:'var(--good)'}}>{Icons.check}</span> Audience confirmed — re-confirm to apply changes.</>
            : previewPending
              ? 'Calculating the audience against current data…'
              : previewError
                ? 'Fix the rule issue above before continuing.'
                : actionMode === 'analyze'
                  ? 'Review the rules and live size, then analyze the audience.'
                  : 'Review the rules and audience size, then confirm to continue.'}
        </div>
        <button
          className={`btn btn-primary ${actionBusy ? 'btn-busy' : ''}`}
          onClick={tryConfirm}
          disabled={actionBusy}
          title={!canConfirm
            ? 'Complete the required rule values and live estimate'
            : actionMode === 'analyze'
              ? 'Analyze this audience definition'
              : undefined}
        >
          {actionBusy
            ? <><span className="btn-spinner" aria-hidden="true"/> Analyzing…</>
            : actionMode === 'analyze'
              ? <>{Icons.search} Analyze audience</>
              : <>{Icons.check} Confirm audience</>}
        </button>
      </div>
    </div>
  );
}

export {
  AudienceBuilder,
  AudienceTypeTabs,
  RulesEditor,
  AudienceSizeGauge,
  RULE_FIELDS,
  defaultRuleValue,
  defaultGroupsForType,
  firstAudienceRuleIssue,
  eligibleLookalikeSeeds,
  canAddAudienceGroup,
  completedAudiencePreview,
  resolvedAudiencePreviewCount,
  prioritizeProduction,
  resolveVisualAudienceRules,
  TYPE_BLURB,
};
